import * as Sentry from "@sentry/react";
import type { ErrorInfo } from "react";
import { isExpectedError } from "@/lib/expected-error";

/**
 * Wraps an error that crashed `QueryErrorBoundary` but which the Sentry
 * `beforeSend` filter classifies as expected (`OfflineError` /
 * `LoginRequiredError`). An expected error reaching the fallback UI is *not*
 * expected — graceful offline or auth handling failed — so the crash must
 * reach Sentry. A distinct error type is what gets it there: `beforeSend`
 * drops the raw `OfflineError` / `LoginRequiredError`, and Sentry's Dedupe
 * integration (which keys on `exception.values[0].type`) will not collapse a
 * `BoundaryCrashError` against the raw event React's `onCaughtError` hook
 * captures in parallel.
 *
 * The original is folded into the message and attached as `extra` — NOT
 * chained via `cause`. A `cause` chain serialises the `OfflineError` into
 * `exception.values[0]`, which is exactly where `dropExpectedEvents`'
 * serialised-type fallback looks: the whole wrapper event would then be
 * dropped. See `specs/conventions/error-reporting.md`.
 */
export class BoundaryCrashError extends Error {
  constructor(original: unknown) {
    const name = original instanceof Error ? original.name : "UnknownError";
    const message =
      original instanceof Error ? original.message : String(original);
    super(`QueryErrorBoundary crashed on ${name}: ${message}`);
    this.name = "BoundaryCrashError";
  }
}

/**
 * `onError` handler for `QueryErrorBoundary`. A normal error is captured as-is.
 * An `OfflineError` / `LoginRequiredError` that crashed the boundary is a bug —
 * offline or auth handling failed to render — but `beforeSend` would drop the
 * raw error as expected, so it is re-reported wrapped in a `BoundaryCrashError`,
 * with the original error and React component stack attached as triage
 * metadata (names and stacks only — never user content).
 */
export function reportBoundaryError(error: unknown, info: ErrorInfo): void {
  if (isExpectedError(error)) {
    Sentry.captureException(new BoundaryCrashError(error), {
      tags: { crashed_boundary: "query-error-boundary" },
      extra: {
        originalError: error instanceof Error ? error.name : String(error),
        originalStack: error instanceof Error ? error.stack : undefined,
        componentStack: info.componentStack,
      },
    });
    return;
  }
  Sentry.captureException(error);
}
